import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, Menu, X } from 'lucide-react';
import { GeoNexoraLogo } from '@/components/common/GeoNexoraLogo';
import { cn } from '@/utils/cn';

export interface AdminNavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  badge?: string;
}

interface AdminSidebarProps {
  navItems: AdminNavItem[];
  activePath: string;
  userLogin?: string;
  /** Desktop (md and up): show only the icon bar. */
  collapsed: boolean;
  /** Phones (below md): the sidebar is slid in over the page. */
  mobileOpen: boolean;
  onToggleCollapse: () => void;
  onCloseMobile: () => void;
  onLogout: () => void;
}

/**
 * Admin navigation. Below md it is an off-canvas drawer over the page; from md up it sits beside the page and can
 * collapse to an icon bar. Which mode applies is decided by Tailwind's md: classes only.
 */
export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  navItems,
  activePath,
  userLogin,
  collapsed,
  mobileOpen,
  onToggleCollapse,
  onCloseMobile,
  onLogout,
}) => {
  const { t } = useTranslation();

  // Hides an element only on desktop while the sidebar is collapsed
  const hiddenWhenCollapsed = collapsed && 'md:hidden';

  return (
    <aside
      id="admin-sidebar"
      className={cn(
        'fixed inset-y-0 left-0 z-40 w-64 bg-gn-primary text-gn-primary transition-all duration-300 overflow-y-auto',
        'md:static md:z-auto md:translate-x-0 md:visible',
        // invisible keeps the closed drawer out of the Tab order
        mobileOpen ? 'translate-x-0' : '-translate-x-full invisible',
        collapsed && 'md:w-20'
      )}
    >
      <div className="h-16 flex items-center justify-between px-4 border-b border-gn">
        <div className={cn('flex items-center gap-2', hiddenWhenCollapsed)}>
          <GeoNexoraLogo size={26} />
          <span className="font-bold text-lg">{t('admin.short')}</span>
        </div>
        <button onClick={onCloseMobile} className="md:hidden p-1 hover:bg-gn-secondary rounded" aria-label={t('admin.close_menu')}>
          <X size={20} />
        </button>
        <button
          onClick={onToggleCollapse}
          className="hidden md:block p-1 hover:bg-gn-secondary rounded"
          aria-label={collapsed ? t('admin.expand_menu') : t('admin.collapse_menu')}
        >
          {collapsed ? <Menu size={20} /> : <X size={20} />}
        </button>
      </div>

      <nav className="flex-1 px-2 py-4 space-y-1">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={onCloseMobile}
            className={cn(
              'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
              activePath === item.path ? 'bg-gn-blue text-gn-inverse' : 'text-gn-primary hover:bg-gn-secondary'
            )}
            title={collapsed ? item.label : undefined}
          >
            {item.icon}
            <div className={cn('flex-1', hiddenWhenCollapsed)}>
              <span className="text-sm font-medium">{item.label}</span>
              {item.badge && <span className="ml-auto text-xs bg-gn-danger px-2 py-1 rounded">{item.badge}</span>}
            </div>
          </Link>
        ))}
      </nav>

      <div className="border-t border-gn p-4">
        {userLogin && (
          <div className={cn('mb-4', hiddenWhenCollapsed)}>
            <p className="text-sm text-gn-secondary">{t('admin.logged_in_as')}</p>
            <p className="font-medium truncate">{userLogin}</p>
          </div>
        )}
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gn-primary hover:bg-gn-secondary transition-colors"
          title={collapsed ? t('menu.logout') : undefined}
        >
          <LogOut size={20} />
          <span className={cn('text-sm font-medium', hiddenWhenCollapsed)}>{t('menu.logout')}</span>
        </button>
      </div>
    </aside>
  );
};
